// src/pages/locations/regions/EditRegionPage.tsx
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  useCountries,
  useRegion,
  useUpdateRegionMutation,
} from "@/api/features/locations";
import RegionForm from "@/components/locations/forms/RegionForm";

import LocationFormPage from "../components/LocationFormPage";
import { useLeaveConfirm } from "../components/form/useLeaveConfirm";
import { useLocationScope } from "../useLocationScope";
import { apiErrorSummary } from "../formErrors";
import { locationTabPath } from "../locations.access";
import { withFlash } from "../flash";

const EditRegionPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: rawId } = useParams<{ id: string }>();
  const id = Number(rawId);

  const { country, scope } = useLocationScope();
  const backTo = locationTabPath("regions", scope);

  const { data: region, isLoading, error: loadError } = useRegion(id);

  const { data: countriesRes, isLoading: countriesLoading } = useCountries({
    limit: 100,
  });
  const countries = countriesRes?.items ?? [];

  const updateMutation = useUpdateRegionMutation();
  const { requestCancel, dialog } = useLeaveConfirm(() => navigate(backTo));

  const error =
    !Number.isFinite(id) || id <= 0
      ? "No region was specified."
      : apiErrorSummary(loadError);

  return (
    <LocationFormPage
      entity="regions"
      title="Edit region"
      subtitle={region?.name}
      backTo={backTo}
      backLabel="Back to Regions"
      isLoading={isLoading}
      loadingLabel="Loading region…"
      error={error}
    >
      {region && (
        <>
          <RegionForm
            mode="edit"
            value={region}
            countries={countries}
            countriesLoading={countriesLoading}
            defaultCountryIso2={region.countryIso2 ?? country}
            isSubmitting={updateMutation.isPending}
            error={updateMutation.error}
            onCancel={requestCancel}
            onSubmit={(values) =>
              updateMutation.mutate(
                {
                  id: region.id,
                  body: {
                    countryIso2: values.countryIso2,
                    name: values.name,
                    code: values.code || null,
                  },
                },
                {
                  onSuccess: (updated) =>
                    // A region can be moved between countries — follow it.
                    navigate(
                      locationTabPath("regions", {
                        country: updated?.countryIso2 ?? values.countryIso2,
                      }),
                      withFlash(`${updated.name} was updated.`)
                    ),
                }
              )
            }
          />

          {dialog}
        </>
      )}
    </LocationFormPage>
  );
};

export default EditRegionPage;
